import {
  ApolloClient,
  ApolloLink,
  createHttpLink,
  InMemoryCache,
  ServerError,
} from "@apollo/client";
import { setContext } from "@apollo/client/link/context";
import { onError } from "@apollo/client/link/error";
import { RetryLink } from "@apollo/client/link/retry";
import { JsqOperationNames } from "~/graphql/mainOperations";
import { getCSRFToken } from "~/utils/csrf";
import { finpubPrimaryFieldsFieldPolicy } from "./fieldPolicies";

// Identifies this app's traffic in WunderGraph Cosmo Studio's Client Usage panel.
// "canopy" rather than the repo name, to match this app's existing Datadog
// service name (deploy.yaml's dd-service) so the two line up when correlating.
// One name for all four links below: they're the same client talking to different
// backends, and Studio already records which endpoint served the request.
// __JSQ_COMMIT_HASH__ is the same build-time constant src/index.tsx already uses
// to version-tag Datadog RUM.
const clientHeaders = {
  "graphql-client-name": "canopy",
  "graphql-client-version": __JSQ_COMMIT_HASH__,
};

const csrfMiddleware = setContext(async () => {
  return {
    headers: {
      "X-CSRF-TOKEN": await getCSRFToken(),
    },
  };
});

const arenaMiddleware = setContext((operation, previousContext) => {
  const { headers, arenaId } = previousContext;
  if (arenaId === undefined) {
    return previousContext;
  }

  return {
    ...previousContext,
    headers: {
      ...headers,
      "JSQ-ARENA": arenaId,
    },
  };
});

export const errorLink = onError(({ networkError, graphQLErrors, operation }) => {
  const fundAdminAuthError = networkError && (networkError as ServerError).statusCode === 401;
  const staffAuthError =
    graphQLErrors &&
    graphQLErrors.filter((e) => e.extensions?.error_code === "STAFF_UNAUTHENTICATED_ACCESS")
      .length > 0;
  const isLoggedOut = fundAdminAuthError || staffAuthError;

  // if the top level app provider failed to resolve the user, let it be responsible
  // for redirection so we don't do a double redirect
  if (
    operation.operationName === JsqOperationNames.Query.GetCurrentUser &&
    operation.getContext().appProvider === true
  ) {
    return;
  }

  if (isLoggedOut && !location.pathname.startsWith("/canopy/login")) {
    location.assign(`/login?path=${encodeURIComponent(location.pathname)}`);
  }
});

// ref: https://www.apollographql.com/docs/react/api/link/apollo-link-retry#default-configuration
const retryLink = new RetryLink({
  delay: {
    initial: Number(process.env.INITIAL_APOLLO_DELAY) || 1000,
    max: Number(process.env.MAX_APOLLO_DELAY) || Infinity,
    jitter: Boolean(process.env.APOLLO_JITTER) || true,
  },
  attempts: {
    max: Number(process.env.MAX_APOLLO_ATTEMPTS) || 3,
    retryIf: (error, _operation) => !!error,
  },
});

const faLink = createHttpLink({
  uri: "/services/fund-accounting/graphql",
  credentials: "same-origin",
  headers: clientHeaders,
});
const staffLink = ApolloLink.from([
  csrfMiddleware,
  arenaMiddleware,
  createHttpLink({
    uri: "/graphql/staff",
    credentials: "same-origin",
    headers: clientHeaders,
  }),
]);

const federationGatewayLink = ApolloLink.from([
  csrfMiddleware,
  retryLink,
  createHttpLink({
    credentials: "same-origin",
    uri: "/service/federation/graphql",
    headers: clientHeaders,
  }),
]);

export const withClientContext = <T extends "staff" | "federation" | "fa">(
  clientName: T,
  context: { [key: string]: unknown; clientName?: never } = {},
): { clientName: T; [key: string]: unknown } => ({
  ...context,
  clientName,
});

const httpLink = ApolloLink.split(
  (operation) => operation.getContext().clientName === "staff",
  staffLink, //if above
  ApolloLink.split(
    (operation) => operation.getContext().clientName === "fa",
    faLink,
    federationGatewayLink,
  ),
);

const buildApolloConfig = () => {
  const baseConfig = {
    cache: new InMemoryCache({
      typePolicies: {
        Query: {
          fields: {
            usersCcTools: {
              keyArgs: ["filters", "includeDeleted"],
              merge(existing, incoming, { args }) {
                if (!args?.after) {
                  return incoming;
                }
                return {
                  ...incoming,
                  edges: [...(existing?.edges || []), ...incoming.edges],
                };
              },
            },
            logs: {
              keyArgs: ["ofType"],
              merge(existing, incoming, { args }) {
                if (!args?.after) {
                  return incoming;
                }
                return {
                  ...incoming,
                  edges: [...(existing?.edges || []), ...incoming.edges],
                };
              },
            },
            finpubPrimaryFields: finpubPrimaryFieldsFieldPolicy,
            arenas: {
              keyArgs: [
                "sortByName",
                "searchTerm",
                "sort",
                "includeAdminArena",
                "applicationContext",
              ],
              merge(existing, incoming, { args }) {
                if (!args?.after || !existing) return incoming;
                return {
                  ...incoming,
                  edges: [...existing.edges, ...incoming.edges],
                };
              },
            },
            arenaReportTemplateSummaries: {
              keyArgs: ["sortBy", "sortDirection", "activeOnly", "search"],
              merge(existing, incoming, { args }) {
                if (!args?.after || !existing) return incoming;
                return {
                  ...incoming,
                  edges: [...existing.edges, ...incoming.edges],
                };
              },
            },
          },
        },
      },
    }),
    link: retryLink.concat(errorLink).concat(httpLink),
  };
  return { ...baseConfig, credentials: "same-origin" };
};

export const client = new ApolloClient(buildApolloConfig());

const networkedActivationLink = ApolloLink.from([
  csrfMiddleware,
  retryLink,
  createHttpLink({
    credentials: "same-origin",
    uri: "/graphql/networked_activation",
    headers: clientHeaders,
  }),
]);
export const networkedActivationClient = new ApolloClient({
  cache: new InMemoryCache(),
  link: retryLink.concat(errorLink).concat(networkedActivationLink),
  credentials: "same-origin",
});
